const CHUNK_RELOAD_FLAG = "osn:chunk-reloaded";

/**
 * Detects errors caused by a stale client build loading chunks that no longer
 * exist (or are incompatible) after a redeploy. Covers explicit `ChunkLoadError`
 * as well as the minified runtime `TypeError`s (e.g. "u is not a function") that
 * surface when an old entry chunk executes against new vendor chunks.
 */
export function isChunkLoadError(error: unknown): boolean {
  if (!error) return false;

  const name = typeof error === "object" && "name" in error ? String((error as { name?: unknown }).name) : "";
  const message =
    typeof error === "string"
      ? error
      : typeof error === "object" && "message" in error
        ? String((error as { message?: unknown }).message)
        : "";

  if (name === "ChunkLoadError") return true;

  return /Loading chunk [\d]+ failed|Failed to load chunk|failed to fetch dynamically imported module|Importing a module script failed|is not a function/i.test(
    message,
  );
}

/**
 * Performs a one-time hard reload to fetch the current build. Guarded by a
 * sessionStorage flag so a persistent failure cannot cause an infinite reload
 * loop. Returns true if a reload was triggered.
 */
export function reloadForChunkError(): boolean {
  if (typeof window === "undefined") return false;

  try {
    if (window.sessionStorage.getItem(CHUNK_RELOAD_FLAG)) return false;
    window.sessionStorage.setItem(CHUNK_RELOAD_FLAG, "1");
  } catch {
    // sessionStorage unavailable (private mode / quota) — reload anyway once.
  }

  window.location.reload();
  return true;
}

/**
 * Clears the reload guard. Called once the app renders successfully so a future
 * deploy can trigger a fresh reload.
 */
export function clearChunkReloadFlag(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
  } catch {
    // ignore
  }
}
